import { OverlayView, Polyline } from '@react-google-maps/api';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { deliveryZonesStore } from '~/mobx';

// Google removed the Drawing library (DrawingManager) from the Maps JavaScript API
// (deprecated Aug 2025, removed May 2026), so polygon drawing is implemented manually:
// every click adds a vertex immediately. Vertices are rendered as a fixed-size pixel dot
// (OverlayView), not `Marker` (deprecated) or `Circle` (radius is in meters, so it turns
// invisible when zoomed out).
//
// Finishing the shape (closing it into the draft zone's `bounds`):
// - double-click anywhere finishes the shape, like the old DrawingManager did. In this
//   Maps API + react-google-maps/api setup a double-click fires a single 'click' before
//   'dblclick' (not two, despite that being the raw DOM norm for plain HTML elements), so
//   only the 1 point that leading click just added gets dropped before finalizing.
// - once there are 3+ points, moving the cursor near the starting point "snaps" to it
//   (the start vertex highlights) — clicking while snapped closes the shape using the
//   existing points, instead of adding a new vertex.
// Save also closes the shape as a fallback (deliveryZonesStore.commitDrawingPoints() in
// DeliveryZonesForm.onSubmit), in case the user reaches Save without explicitly closing it.
//
// Points live in the store (`drawingPoints`), not local state, so the Save button — in a
// different component — can read/close them.
//
// While drawing, a "rubber band" segment follows the cursor from the last placed vertex,
// like the old DrawingManager did. It isn't a real vertex — `cursorPosition` is just
// appended to the rendered path on top of the committed points, and it snaps exactly onto
// the start point while `isSnappedToStart` so the closing segment lines up precisely.
//
// The crosshair cursor is applied via `mapStore.setOption`, the same observable-driven
// options object the map is already rendered with (`mapStore.props` -> `<Map
// options=...>` -> `<GoogleMap>`) — NOT a raw `map.setOptions()` call, so it survives
// whatever re-render/prop-sync the map wrapper performs.
const VERTEX_SIZE = 10; // px, constant regardless of zoom level
const START_VERTEX_SNAP_SIZE = 12; // px, size of the start vertex while snapped
const SNAP_PIXEL_RADIUS = 12; // px, how close the cursor must be to the start point to snap/close

// meters-per-pixel at a given latitude/zoom (standard Web Mercator approximation) — used to
// convert our fixed on-screen snap radius into a geo distance for hit-testing, so the snap
// radius stays visually constant regardless of zoom level
const metersPerPixel = (lat: number, zoom: number) => (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom);

const DeiveryZoneDrawingTools = observer(() => {
  const [isSnappedToStart, setIsSnappedToStart] = useState(false);
  const [cursorPosition, setCursorPosition] = useState<google.maps.LatLngLiteral | null>(null);

  const shouldDraw = Boolean(deliveryZonesStore.draft) && !deliveryZonesStore.draft?.bounds.length;
  const map = deliveryZonesStore.mapStore.map;

  useEffect(() => {
    if (!shouldDraw || !map) {
      return;
    }

    deliveryZonesStore.resetDrawingPoints();
    setIsSnappedToStart(false);
    setCursorPosition(null);

    deliveryZonesStore.mapStore.setOption({ draggableCursor: 'crosshair' });

    const isNearStart = (latLng: google.maps.LatLng): boolean => {
      const drawingPoints = deliveryZonesStore.drawingPoints;
      if (drawingPoints.length < 3) return false;

      const start = new google.maps.LatLng(drawingPoints[0]);
      const distanceMeters = google.maps.geometry.spherical.computeDistanceBetween(latLng, start);
      const threshold = metersPerPixel(latLng.lat(), map.getZoom() || 0) * SNAP_PIXEL_RADIUS;

      return distanceMeters <= threshold;
    };

    const handleMouseMove = (event: google.maps.MapMouseEvent) => {
      if (!event.latLng) return;

      setCursorPosition(event.latLng.toJSON());
      setIsSnappedToStart(isNearStart(event.latLng));
    };

    const handleMouseOut = () => {
      setCursorPosition(null);
    };

    const handleClick = (event: google.maps.MapMouseEvent) => {
      if (!event.latLng) return;

      if (isNearStart(event.latLng)) {
        deliveryZonesStore.commitDrawingPoints();
        setIsSnappedToStart(false);
        return;
      }

      deliveryZonesStore.addDrawingPoint(event.latLng.toJSON());
    };

    const handleDoubleClick = () => {
      // drop the 1 point the double-click's own leading click just added (see comment above)
      const withoutNoise = deliveryZonesStore.drawingPoints.slice(0, -1);

      deliveryZonesStore.setDrawingPoints(withoutNoise);

      if (!deliveryZonesStore.commitDrawingPoints()) {
        setIsSnappedToStart(false);
      }
    };

    const clickListener = map.addListener('click', handleClick);
    const dblClickListener = map.addListener('dblclick', handleDoubleClick);
    const mouseMoveListener = map.addListener('mousemove', handleMouseMove);
    const mouseOutListener = map.addListener('mouseout', handleMouseOut);

    return () => {
      clickListener.remove();
      dblClickListener.remove();
      mouseMoveListener.remove();
      mouseOutListener.remove();
      deliveryZonesStore.mapStore.setOption({ draggableCursor: undefined });
    };
  }, [shouldDraw, map]);

  const points = deliveryZonesStore.drawingPoints;

  if (!shouldDraw || !points.length) return null;

  const color = deliveryZonesStore.draft?.color ?? '#4285F4';

  // rubber-band: extend the confirmed path with the live cursor position (snapped exactly
  // onto the start point while `isSnappedToStart`) so the line visually follows the cursor
  // up to the next click, like the old DrawingManager did.
  const liveEndpoint = isSnappedToStart ? points[0] : cursorPosition;
  const linePath = liveEndpoint ? [...points, liveEndpoint] : points;

  return (
    <>
      {points.map((point, index) => {
        const isStart = index === 0;
        const snapped = isStart && isSnappedToStart;
        const size = snapped ? START_VERTEX_SNAP_SIZE : VERTEX_SIZE;

        return (
          <OverlayView
            key={`${point.lat},${point.lng}-${index}`}
            position={point}
            mapPaneName={OverlayView.OVERLAY_LAYER}
            getPixelPositionOffset={() => ({ x: -size / 2, y: -size / 2 })}>
            <div
              style={{
                width: size,
                height: size,
                borderRadius: '50%',
                background: snapped ? '#fff' : color,
                border: `2px solid ${snapped ? color : '#fff'}`,
                boxShadow: snapped ? `0 0 0 4px ${color}88` : '0 0 0 1px rgba(0, 0, 0, 0.35)',
                pointerEvents: 'none',
                transition: 'width 120ms ease, height 120ms ease, box-shadow 120ms ease',
              }}
            />
          </OverlayView>
        );
      })}

      {linePath.length >= 2 && <Polyline path={linePath} options={{ strokeColor: color, clickable: false }} />}
    </>
  );
});

export default DeiveryZoneDrawingTools;
